import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type SiteRole = 'admin' | 'supervisor' | 'security' | 'viewer';

export type FeatureKey =
  | 'scan'
  | 'tours'
  | 'rounding'
  | 'dar'
  | 'storage'
  | 'census'
  | 'alerts';

export type SiteFeatures = Record<FeatureKey, boolean>;

export const ALL_FEATURES_ENABLED: SiteFeatures = {
  scan: true,
  tours: true,
  rounding: true,
  dar: true,
  storage: true,
  census: true,
  alerts: true,
};

export interface SiteAccessEntry {
  siteId: string;
  siteSlug: string;
  siteName: string;
  role: SiteRole;
  notifyViaSms: boolean;
  notifyViaEmail: boolean;
  features: SiteFeatures;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    displayName: string;
    phoneE164: string | null;
  };
  globalRoles: string[];
  siteAccess: SiteAccessEntry[];
}

interface AuthContextValue {
  me: MeResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  isGlobalAdmin: boolean;
  refetch: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const query = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/me'),
    staleTime: 60_000,
    retry: false,
  });

  const value: AuthContextValue = {
    me: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    isGlobalAdmin: !!query.data?.globalRoles.includes('Global.Admin'),
    refetch: () => query.refetch(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
